import defaultCmeThumbnail from "../../../assets/images/empoweringdoctors-1.jpg";
import defaultDoctorAvatar from "../../../assets/images/docter-profile.png";
import { UPLOADS_BASE } from "../constants/api";
import { slugifyCmeTitle } from "../../../api/cmeApi";
import { formatArticleDate } from "./newsArticleHelpers";
import { getMediaThumbnailUrl } from "./youtubeHelpers";

export const formatCmeTags = (value) => {
  if (!value) return [];
  return String(value)
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => (t.startsWith("#") ? t : `#${t}`));
};

export const formatCmeDuration = (minutes) => {
  if (minutes == null || minutes === "") return "";
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `${n} Min`;
};

export const mapCmeToCardProps = (cme) => {
  const specialties = (cme.specialties || [])
    .map((s) => s.medical_specialty)
    .filter(Boolean)
    .join(", ");

  const firstRef = (cme.references || [])[0];
  const referenceUrl = firstRef
    ? `${UPLOADS_BASE}/uploads/${firstRef.file_path}`
    : undefined;

  return {
    id: cme.digital_cme_id,
    title: cme.cme_title,
    description: cme.short_description || "",
    doctorLabel: cme.doctor_id ? `Doctor ID: ${cme.doctor_id}` : "",
    specialties,
    duration: formatCmeDuration(cme.duration_minutes),
    tags: formatCmeTags(cme.context_tags),
    mediaLink: cme.media_link || undefined,
    referenceUrl,
  };
};

const formatDoctorDisplayName = (name) => {
  if (!name) return "";
  return String(name).replace(/^Dr\.?\s*/i, "").trim() || String(name).trim();
};

const resolveDoctorFromLookup = (cme, doctorsById = {}) => {
  if (!cme?.doctor_id) return null;
  return doctorsById[cme.doctor_id] || doctorsById[String(cme.doctor_id)] || null;
};

const resolveDoctorAvatar = (cme, doctor) => {
  const profilePic = cme.doctor_profile_pic_path || doctor?.profile_pic_path;
  if (profilePic) {
    return `${UPLOADS_BASE}/uploads/${profilePic}`;
  }
  return defaultDoctorAvatar;
};

const resolveAuthorName = (cme, doctor) => {
  const name = cme.doctor_name || doctor?.doctor_name;
  if (name) return `By Doctor ${formatDoctorDisplayName(name)}`;
  return "By Doctor";
};

const resolveAuthorRole = (cme, specialtyLabels, doctor) => {
  const doctorRole = [
    cme.doctor_designation || doctor?.designation,
    cme.doctor_medical_specialty || doctor?.medical_specialty,
    cme.establishment_name || doctor?.establishment_name,
  ]
    .filter(Boolean)
    .join(", ");
  return doctorRole || specialtyLabels || "—";
};

export const mapCmeToDetailProps = (cme, doctorsById = {}) => {
  const doctor = resolveDoctorFromLookup(cme, doctorsById);
  const specialtyLabels = (cme.specialties || [])
    .map((s) => s.medical_specialty)
    .filter(Boolean);

  const referenceImages = (cme.references || [])
    .map((ref) => ref.file_path)
    .filter(Boolean)
    .map((path) => `${UPLOADS_BASE}/uploads/${path}`);

  const uploadThumbnail = referenceImages[0] || null;
  const thumbnail = getMediaThumbnailUrl(cme.media_link) || uploadThumbnail || defaultCmeThumbnail;

  return {
    id: cme.digital_cme_id,
    slug: slugifyCmeTitle(cme.cme_title, cme.digital_cme_id),
    title: cme.cme_title || "CME Session",
    shortDescription: cme.short_description?.trim() || "",
    longDescription: cme.long_description?.trim() || "",
    contextTags: cme.context_tags?.trim() || "",
    publishedOn: formatArticleDate(cme.created_on),
    lastUpdatedOn: formatArticleDate(cme.last_updated_on),
    duration: formatCmeDuration(cme.duration_minutes),
    durationMinutes: cme.duration_minutes ?? null,
    thumbnail,
    mediaLink: cme.media_link || undefined,
    hasPlay: Boolean(cme.media_link),
    doctorName: cme.doctor_name || doctor?.doctor_name || "",
    doctorDesignation: cme.doctor_designation || doctor?.designation || "",
    doctorSpecialty: cme.doctor_medical_specialty || doctor?.medical_specialty || "",
    establishmentName: cme.establishment_name || doctor?.establishment_name || "",
    authorAvatar: resolveDoctorAvatar(cme, doctor),
    specialtyLabels,
    referenceImages,
    tags: formatCmeTags(cme.context_tags),
  };
};

export const mapCmeToVideoCardProps = (cme, doctorsById = {}) => {
  const doctor = resolveDoctorFromLookup(cme, doctorsById);
  const specialties = (cme.specialties || [])
    .map((s) => s.medical_specialty)
    .filter(Boolean)
    .join(", ");

  const thumbnail = getMediaThumbnailUrl(cme.media_link) || defaultCmeThumbnail;

  return {
    id: cme.digital_cme_id,
    slug: slugifyCmeTitle(cme.cme_title, cme.digital_cme_id),
    thumbnail,
    duration: formatCmeDuration(cme.duration_minutes) || "—",
    date: formatArticleDate(cme.created_on),
    title: cme.cme_title,
    description: cme.short_description || "",
    authorAvatar: resolveDoctorAvatar(cme, doctor),
    authorName: resolveAuthorName(cme, doctor),
    authorRole: resolveAuthorRole(cme, specialties, doctor),
    hasPlay: Boolean(cme.media_link),
    mediaLink: cme.media_link || undefined,
  };
};
